/**
 * @file        src/pages/platform/TenantBrandsPage.jsx
 * @description Tenant Management → Brands. The selected tenant's storefront
 *              registry (`brands` in the TENANT's own DB) — codes are 3 chars,
 *              unique, immutable; at least one active brand is mandatory before
 *              go-live. Moved out of the tenant edit page's stacked panels into
 *              its own sidebar entry (?tenant=<id> picks the tenant). Each
 *              brand now also carries its STOREFRONT logo + favicon
 *              (migrations 039/050): what the customer booking site shows.
 *              The tenant admin can update those two images later via
 *              Site Management → Site Setting; everything else here stays
 *              super_admin-only.
 * @author      Kinetika Labs
 * @created     2026-08-10
 * @changelog
 *   - 2026-08-10 Claude — Initial (extracted from AddTenant.jsx; adds per-brand
 *                          logo/favicon and a row edit form).
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import PageHead from '../../components/PageHead';
import Icon from '../../components/Icon';
import Chip from '../../components/Chip';
import ImageUpload from '../../components/ImageUpload';
import { get, post, patch } from '../../api/client';
import { qk } from '../../api/queryKeys';
import { toast } from '../../lib/toast';
import { LOGO_CHECK, LOGO_HINT, FAVICON_CHECK, FAVICON_HINT } from '../../lib/brandingSpecs';
import TenantPicker, { useTenantParam } from './TenantPicker';
import Field from './Field';

export function useTenantBrands(tenantId) {
  return useQuery({
    queryKey: qk.tenants.brands(tenantId),
    queryFn: () => get(`/tenants/${tenantId}/brands`).then((r) => r.data),
    enabled: Boolean(tenantId),
  });
}

export default function TenantBrandsPage() {
  const [tenantId, setTenantId] = useTenantParam();
  return (
    <>
      <PageHead title="Brands" subtitle="Platform · Tenant Management · storefront registry" />
      <article className="panel glass" style={{ marginBottom: 16 }}>
        <TenantPicker value={tenantId} onChange={setTenantId} />
      </article>
      {tenantId ? <TenantBrands tenantId={tenantId} /> : (
        <article className="panel glass"><div className="empty">Select a tenant to manage its brands.</div></article>
      )}
    </>
  );
}

function TenantBrands({ tenantId }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null); // null | 'new' | brand row
  const { data, isLoading, isError } = useTenantBrands(tenantId);
  const rows = data || [];
  const activeCount = rows.filter((b) => Number(b.status) === 1).length;

  const refresh = () => {
    qc.invalidateQueries({ queryKey: qk.tenants.brands(tenantId) });
    qc.invalidateQueries({ queryKey: qk.tenants.readiness(tenantId) });
    setEditing(null);
  };

  const toggle = useMutation({
    mutationFn: (b) => patch(`/tenants/${tenantId}/brands/${b.id}`, { status: Number(b.status) === 1 ? 0 : 1 }),
    onSuccess: refresh,
    onError: (e) => toast.err('Could not update brand', e.response?.data?.error?.message || 'Failed'),
  });

  return (
    <article className="panel glass">
      <div className="panel-head">
        <div>
          <h2>Brands</h2>
          <p className="muted">
            The storefronts this tenant's database serves. At least one active brand is required
            before go-live — gateways, coupons and pricing are all scoped to a brand code, and the
            booking site refuses requests for a tenant with none. The logo and favicon here are the
            BOOKING portal's (the customer site); the admin console's live under Admin Portal Branding.
          </p>
        </div>
        {editing === null ? (
          <button className="btn primary" onClick={() => setEditing('new')}>
            <Icon name="plus" /> Add Brand
          </button>
        ) : null}
      </div>

      {isLoading ? (
        <div className="empty">Loading brands…</div>
      ) : isError ? (
        <div className="empty"><p className="field-error">Could not load this tenant's brands.</p></div>
      ) : (
        <>
          {rows.length === 0 && editing === null ? (
            <div className="empty">
              <p className="field-error">No brand configured — this tenant cannot serve any storefront.</p>
            </div>
          ) : null}

          {rows.length ? (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr><th>Code</th><th>Name</th><th>Logo</th><th>Storefront Origins</th><th>Status</th><th>Action</th></tr>
                </thead>
                <tbody>
                  {rows.map((b) => (
                    <tr key={b.id}>
                      <td><b>{b.code}</b></td>
                      <td>{b.name}</td>
                      <td>
                        {b.logo_url
                          ? <img src={b.logo_url} alt={`${b.name} logo`} style={{ height: 24, maxWidth: 120, objectFit: 'contain' }} />
                          : <Chip tone="rej">Missing</Chip>}
                      </td>
                      <td className="muted">{b.frontend_origins || '—'}</td>
                      <td>{Number(b.status) === 1 ? <Chip tone="ok">Active</Chip> : <Chip tone="rej">Disabled</Chip>}</td>
                      <td className="row-act">
                        <button className="ib edit" aria-label="Edit" onClick={() => setEditing(b)}>
                          <Icon name="edit" />
                        </button>
                        <button
                          className="ib edit"
                          aria-label={Number(b.status) === 1 ? 'Disable' : 'Enable'}
                          title={Number(b.status) === 1 && activeCount <= 1
                            ? 'Cannot disable the last active brand' : undefined}
                          disabled={toggle.isPending || (Number(b.status) === 1 && activeCount <= 1)}
                          onClick={() => toggle.mutate(b)}
                        >
                          <Icon name={Number(b.status) === 1 ? 'x' : 'check'} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          {editing !== null ? (
            <BrandForm
              tenantId={tenantId}
              brand={editing === 'new' ? null : editing}
              onSaved={refresh}
              onCancel={() => setEditing(null)}
            />
          ) : null}
        </>
      )}
    </article>
  );
}

function BrandForm({ tenantId, brand, onSaved, onCancel }) {
  const isNew = !brand;
  const [form, setForm] = useState(isNew
    ? { code: '', name: '', logo_url: '', favicon_url: '', frontend_origins: '' }
    : {
      code: brand.code,
      name: brand.name || '',
      logo_url: brand.logo_url || '',
      favicon_url: brand.favicon_url || '',
      frontend_origins: brand.frontend_origins || '',
    });
  const [errors, setErrors] = useState({});

  const mut = useMutation({
    mutationFn: (body) => (isNew
      ? post(`/tenants/${tenantId}/brands`, body)
      : patch(`/tenants/${tenantId}/brands/${brand.id}`, body)),
    onSuccess: () => {
      toast.ok(isNew ? 'Brand added' : 'Brand updated', `${form.name} (${form.code.toLowerCase()}) saved.`);
      onSaved();
    },
    onError: (e) => {
      const fields = e.response?.data?.error?.fields;
      if (fields) setErrors(fields);
      toast.err('Could not save brand', e.response?.data?.error?.message || 'Validation failed');
    },
  });

  function submit(e) {
    e.preventDefault();
    const fe = {};
    if (isNew && !/^[A-Za-z0-9]{3}$/.test(form.code.trim())) fe.code = 'Exactly 3 letters/digits';
    if (form.name.trim().length < 2) fe.name = 'Required';
    if (Object.keys(fe).length) return setErrors(fe);
    setErrors({});
    const body = {
      name: form.name.trim(),
      logo_url: form.logo_url,
      favicon_url: form.favicon_url,
      frontend_origins: form.frontend_origins.trim(),
    };
    if (isNew) {
      body.code = form.code.trim().toLowerCase();
      body.status = 1;
    }
    mut.mutate(body);
  }

  const on = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <form className="form-grid" onSubmit={submit} style={{ marginTop: 12 }}>
      <div className="field full">
        <label className="section-label">{isNew ? 'New brand' : `Editing: ${brand.name} (${brand.code})`}</label>
      </div>
      {isNew ? (
        <Field label="Brand Code" required err={errors.code}
               hint="Exactly 3 characters, e.g. neo. Immutable once created — it is stamped on every booking, coupon and gateway row.">
          <input value={form.code} onChange={on('code')} placeholder="e.g. neo" maxLength={3} />
        </Field>
      ) : null}
      <Field label="Brand Name" required err={errors.name}>
        <input value={form.name} onChange={on('name')} placeholder="e.g. Beyond Destination" />
      </Field>
      <Field label="Storefront Origins" err={errors.frontend_origins}
             hint="Optional, comma-separated. Lets the booking backend resolve this brand from the browser Origin when the X-Brand header is missing.">
        <input value={form.frontend_origins} onChange={on('frontend_origins')} placeholder="https://beyonddestination.in" />
      </Field>
      <Field label="Storefront Logo" err={errors.logo_url}
             hint={`Shown on the customer booking site and in its transactional emails. ${LOGO_HINT}`}>
        <ImageUpload value={form.logo_url} check={LOGO_CHECK}
                     onChange={(v) => setForm((f) => ({ ...f, logo_url: v }))} />
      </Field>
      <Field label="Storefront Favicon" err={errors.favicon_url}
             hint={`The booking site's browser-tab icon. ${FAVICON_HINT}`}>
        <ImageUpload value={form.favicon_url} check={FAVICON_CHECK}
                     onChange={(v) => setForm((f) => ({ ...f, favicon_url: v }))} />
      </Field>
      <div className="form-actions">
        <button className="btn primary" type="submit" disabled={mut.isPending}>
          <Icon name="check" /> {mut.isPending ? 'Saving…' : isNew ? 'Add Brand' : 'Save Brand'}
        </button>
        <button className="btn ghost" type="button" onClick={onCancel}><Icon name="x" /> Cancel</button>
      </div>
    </form>
  );
}
